/**
 * The cycle's beta-hCG measurements, from the one endpoint that serves every
 * modality.
 *
 * `GET /cycles/{cycleId}/beta-hcg-observations` unions canonical clinical
 * observations with the legacy rows that have no canonical link yet, keyed on
 * LOINC 21198-7. It returns one deduplicated, chronologically ordered series,
 * so an FET cycle and an IVF cycle answer the same question the same way
 * (#3490).
 *
 * This hook is the measurement source. `useFETBetaLifecycle` remains the
 * source for FET *workflow* — order status, collection windows, missed and
 * cancelled draws, and the clinic's interpretations — none of which is a
 * measurement.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { BetaHcgSeriesItem } from "./presenters/betaHcgSeries";

export type { BetaHcgSeriesItem };

export function betaHcgSeriesQueryKey(
  patientId: string | null,
  cycleId: string | null,
) {
  return ["beta-hcg-series", patientId, cycleId] as const;
}

export function useBetaHcgSeries(options: {
  cycleId: string | null;
  enabled?: boolean;
}) {
  const { client, patientId } = useApiClient();
  const enabled = options.enabled ?? true;

  return useQuery<BetaHcgSeriesItem[]>({
    queryKey: betaHcgSeriesQueryKey(patientId, options.cycleId),
    queryFn: async () => {
      if (!options.cycleId) return [];

      const response = await client.GET(
        "/cycles/{cycleId}/beta-hcg-observations",
        { params: { path: { cycleId: options.cycleId } } },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to load beta-hCG results",
          "beta-hcg-series",
        );
      }

      return response.data?.data ?? [];
    },
    enabled: enabled && !!patientId && !!options.cycleId,
  });
}
